import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionExpired } from '../context/SessionExpiredContext';
import './SessionExpiredModal.css';

const SessionExpiredModal: React.FC = () => {
  const { isSessionExpired, expiredUserRole, clearSessionExpired } = useSessionExpired();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isSessionExpired) {
      // Clear any stored auth data
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      
      // Trigger entrance animation
      setIsVisible(true);
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    }
  }, [isSessionExpired]);

  const handleOkClick = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      
      // Determine redirect path based on user role
      let redirectPath = '/login'; // Default for end users
      
      if (expiredUserRole === 'business-admin' || expiredUserRole === 'super-admin') {
        redirectPath = '/admin/login';
      }
      
      clearSessionExpired();
      navigate(redirectPath, { replace: true });
    }, 300);
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div className={`session-expired-overlay ${isAnimating ? 'visible' : ''}`}>
      <div className={`session-expired-modal ${isAnimating ? 'visible' : ''}`}>
        {/* Decorative background elements */}
        <div className="modal-background-decoration">
          <div className="decoration-circle circle-1"></div>
          <div className="decoration-circle circle-2"></div>
          <div className="decoration-circle circle-3"></div>
        </div>
        
        {/* Icon Section */}
        <div className="modal-icon-container">
          <div className="icon-ring">
            <div className="icon-inner">
              <svg 
                className="lock-icon" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  d="M19 11H5C3.89543 11 3 11.8954 3 13V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V13C21 11.8954 20.1046 11 19 11Z" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
                <path 
                  d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="16" r="1.5" fill="currentColor" />
              </svg>
            </div>
          </div>
          <div className="pulse-ring pulse-1"></div>
          <div className="pulse-ring pulse-2"></div>
        </div>

        {/* Content Section */}
        <div className="modal-content">
          <h2 className="modal-title">Session Expired</h2>
          <div className="modal-divider"></div>
          <p className="modal-message">
            Your session has ended due to a period of inactivity. 
            For your security, you have been automatically logged out.
          </p>
          <p className="modal-submessage">
            Please sign in again to continue where you left off.
          </p>
        </div>

        {/* Action Section */}
        <button 
          className="modal-button"
          onClick={handleOkClick}
        >
          <span className="button-text">OK, Sign Me In</span>
          <svg 
            className="button-arrow" 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path 
              d="M5 12H19M19 12L12 5M19 12L12 19" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Security Badge */}
        <div className="security-badge">
          <svg 
            className="shield-icon" 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path 
              d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" 
              stroke="currentColor" 
              strokeWidth="1.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
            <path 
              d="M9 12L11 14L15 10" 
              stroke="currentColor" 
              strokeWidth="1.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
          <span>Protected by LinkWell Security</span>
        </div>
      </div>
    </div>
  );
};

export default SessionExpiredModal;

